import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AssetType } from '@modules/cards/types/asset.types';
import { TagMenu } from '@modules/shared/schemas/tag.schema';
import { FilterService } from '@modules/shared/services/filter.service';
import { SearchService } from '@modules/shared/services/search.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'mb-filtertool-narrow',
  templateUrl: './filtertool-narrow.component.html',
  styleUrls: ['./filtertool-narrow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiltertoolNarrowComponent implements OnInit, OnChanges, OnDestroy {
  public flattenedTags: TagMenu[] = [];
  public activeTagMenus: TagMenu[] = [];
  public currentLanguage: string;
  public filteredTags: { [key: string]: TagMenu[] } = {
    DOCUMENT: [],
    IMAGE: [],
    MEDIA_COLLECTION: [],
    MEDIA_SPECIAL: [],
    PRESS_INFORMATION: [],
    PRESS_KIT: [],
    VIDEO: [],
    AUDIO: [],
    EVENT: [],
    PRESS_CONTACT: [],
    TECHNICAL_DATA: [],
  };
  public activeTags: { [key: string]: number[] } = {};
  public availableTags: { [key: string]: number[] } = {};

  private _destroy$ = new Subject<boolean>();

  @Input() filterGroups!: number[];
  @Input() activeEntity!: AssetType;

  @Output() allFiltersClickEvent = new EventEmitter();

  constructor(
    private translateService: TranslateService,
    private searchService: SearchService,
    private cd: ChangeDetectorRef,
    private filterService: FilterService
  ) {
    this.currentLanguage = this.translateService.currentLang;
    this.translateService.onLangChange.pipe(takeUntil(this._destroy$)).subscribe((event: LangChangeEvent) => {
      this.currentLanguage = event.lang;
    });
  }

  ngOnInit(): void {
    this.searchService.tags$.pipe(takeUntil(this._destroy$)).subscribe((data: TagMenu[] | null) => {
      if (data) {
        this.flattenedTags = data.flatMap((tag: TagMenu) => {
          return tag.children ? [tag, ...tag.children] : [tag];
        });

        this.activeTagMenus = this.filterGroups
          .map((filterId) => {
            return this.flattenedTags.find((x: TagMenu) => x.id === filterId);
          })
          .filter(Boolean) as TagMenu[];
      }

      this.cd.detectChanges();
    });

    this.filterService.filteredTags$.pipe(takeUntil(this._destroy$)).subscribe((tags: { [key: string]: TagMenu[] }) => {
      this.filteredTags = tags;
      this.cd.detectChanges();
    });

    this.filterService.activeTags$.pipe(takeUntil(this._destroy$)).subscribe((tags: { [key: string]: number[] }) => {
      this.activeTags = tags;
      this.cd.detectChanges();
    });

    this.filterService.availableTags$.pipe(takeUntil(this._destroy$)).subscribe((tags: { [key: string]: number[] }) => {
      this.availableTags = tags;
      this.cd.detectChanges();
    });
  }

  ngOnChanges() {
    this.activeTagMenus = this.filterGroups
      .map((filterId) => {
        return this.flattenedTags.find((x: TagMenu) => x.id === filterId);
      })
      .filter(Boolean) as TagMenu[];
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  /**
   * Counts how many tags from 'tags' list (which represents specific multiselect) is also in 'available tags' list.
   * @returns false value if count is equal to 0, else true
   */
  public isMultiSelectAvailable(tags: TagMenu[]) {
    return Boolean(tags?.filter((tag) => this.availableTags[this.activeEntity].includes(tag.id)).length || 0);
  }

  /**
   * Counts how many tags from 'tags' list (which represents specific multiselect) is also in 'active tags' list.
   * @returns true value if count is equal to 0, else false
   */
  public isMultiSelectDisabled(tags: TagMenu[]) {
    return !Boolean(tags?.filter((tag) => this.activeTags[this.activeEntity].includes(tag.id)).length || 0);
  }

  /**
   * Checks if tag is in list of available tags.
   * @returns boolean value of the checking statement
   */
  public isTagAvailable(id: number) {
    return this.availableTags[this.activeEntity].includes(id);
  }

  /**
   * Checks if tag is in list of active tags.
   * @returns the opposite boolean value of the checking statement
   */
  public isTagDisabled(id: number) {
    return !this.activeTags[this.activeEntity].includes(id);
  }

  public filterTag(e: any, tag: TagMenu) {
    if (e.detail.checked) {
      this.filterService.addTag(tag, this.activeEntity);
    } else {
      this.filterService.removeTag(tag, this.activeEntity);
    }
  }

  public filterTags(e: any, tags: TagMenu[]) {
    e.detail.forEach((el: any) => {
      const isTagChecked = el.checked;
      const isTagFiltered = this.filteredTags[this.activeEntity].find((tag) => tag.id == el.value);
      if (isTagChecked && !isTagFiltered) {
        this.filteredTags[this.activeEntity].push(tags.find((tag) => tag.id == el.value)!);
      } else if (!isTagChecked && isTagFiltered) {
        this.filteredTags[this.activeEntity] = this.filteredTags[this.activeEntity].filter(
          (item) => item.id != el.value
        );
      }
    });
    this.filterService.set(this.filteredTags[this.activeEntity], this.activeEntity);
  }

  public removeTag(tag: TagMenu) {
    this.filterService.removeTag(tag, this.activeEntity);
  }

  public isChecked(tagId: number) {
    return this.filteredTags[this.activeEntity].some((item) => item.id === tagId);
  }

  public reset() {
    this.filterService.reset(this.activeEntity);
  }

  public allFilters() {
    this.allFiltersClickEvent.emit();
  }

  public getCheckboxCount(parentTagId: number) {
    let counter = 0;
    this.filteredTags[this.activeEntity].forEach((tag) => {
      if (tag.parents.includes(parentTagId.toString())) {
        counter++;
      }
    });
    return counter;
  }

  public resetTagMenu(parentTagId: number) {
    this.filterService.resetTagMenu(parentTagId, this.activeEntity);
  }
}
